import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { OnboardingService } from '../onboarding.service';
import { FormBuilder, Validators } from '@angular/forms';
import { ArivoStepperComponent } from '../../../../shared/components/ui-components/arivo-stepper/arivo-stepper.component';
import { LicensePlate } from '../../../../shared/models/licensePlate';
import { fbOptionalString } from '../../../../shared/utils/form.utils';
import { ToastService } from '../../../../shared/services/toast.service';
import { showSpinner } from '../../../../shared/utils/common.utils';
import { TranslateService } from '@ngx-translate/core';
import { ArivoMarketingConsentService } from '../../../../shared/components/arivo-marketing-consent/arivo-marketing-consent.service';
import { FeatureFlagService } from '../../../../core/services/feature-flag.service';
import { ProviderLinkOptions } from '../../../../shared/models/providerSettings';
import { ProviderSettingsService } from '../../../../core/services/provider-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-onboarding-third-step',
  templateUrl: './onboarding-third-step.component.html',
  styleUrl: './onboarding-third-step.component.scss',
})
export class OnboardingThirdStepComponent implements OnInit {
  @Input() stepperComponent?: ArivoStepperComponent;

  destroyRef = inject(DestroyRef);
  emailSent: boolean = false;
  email?: string;
  marketingConsentEnabled!: boolean;
  providerLinks!: ProviderLinkOptions;

  constructor(
    private _formBuilder: FormBuilder,
    private translateService: TranslateService,
    private onboardingService: OnboardingService,
    private toastService: ToastService,
    private marketingConsentService: ArivoMarketingConsentService,
    private providerSettingsService: ProviderSettingsService,
    private featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit() {
    this.marketingConsentEnabled = this.featureFlagService.getFeatureFlag('enable_marketing_consent');

    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });
  }

  get licensePlate(): LicensePlate | null {
    return this.onboardingService.licensePlate.getValue();
  }

  signUpForm = this._formBuilder.group({
    email: [fbOptionalString, [Validators.required, Validators.email]],
    termsAndConditions: [false, [Validators.requiredTrue]],
    marketingConsent: [false],
  });

  previous() {
    if (this.stepperComponent) {
      this.stepperComponent.selectedIndex = 0;
    }
  }

  next() {
    this.signUpForm.markAllAsTouched();
    if (this.signUpForm.valid && this.licensePlate) {
      this.onboardingService
        .sendOnboardingEmail(this.licensePlate, this.signUpForm.value.email!, this.signUpForm.value.marketingConsent!)
        .pipe(showSpinner())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.emailSent = true;
            this.email = this.signUpForm.value.email!;
            this.toastService.add({
              message: this.translateService.instant('Onboarding.WeHaveSentAnEmailToast', { email: this.signUpForm.value.email! }),
              type: 'success',
            });
            if (this.signUpForm.value.marketingConsent) {
              this.marketingConsentService.submitMarketingAgreement(this.signUpForm.value.email!);
            }
          },
          error: (error) => {
            if (error.status === 429) {
              this.toastService.add({
                message: error.error.message ?? this.translateService.instant('Common.Errors.UnknownError'),
                type: 'error',
              });
            } else {
              this.toastService.add({
                message: this.translateService.instant('LogIn.CouldNotSendEmail'),
                type: 'error',
              });
            }
          },
        });
    }
  }
}
